import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MagnifyingGlass, CaretLeft, CaretRight, ArrowCounterClockwise, Trash, ArrowCircleUp, ArrowCircleDown } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWorkspacesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 25;
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delName, setDelName] = useState("");
  const [delCheck, setDelCheck] = useState(false);
  const [busy, setBusy] = useState(false);
  // Batch 62-bis: promote business→agency
  const [promoTarget, setPromoTarget] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  // Batch 66: demote agency→business
  const [demoTarget, setDemoTarget] = useState(null);
  const [demoForce, setDemoForce] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);

  const handleDemote = async () => {
    if (!demoTarget || demoBusy) return;
    setDemoBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/workspaces/${demoTarget.id}/demote-to-business`,
        { force: demoForce }, { withCredentials: true });
      if (data?.already) toast.info(`${data.email || demoTarget.owner_email} è già Azienda`);
      else toast.success(`${data.email || demoTarget.owner_email} declassato ad Azienda${data.orphaned_clients ? ` (${data.orphaned_clients} client ws orfani)` : ""}`);
      setDemoTarget(null); setDemoForce(false);
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore";
      toast.error(msg);
      // Se 409 (active clients), mantieni dialog aperto e mostra force option
    }
    finally { setDemoBusy(false); }
  };

  const handlePromote = async () => {
    if (!promoTarget || promoBusy) return;
    setPromoBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/workspaces/${promoTarget.id}/promote-to-agency`, {}, { withCredentials: true });
      if (data?.already) toast.info(`${data.email} è già un account Agenzia`);
      else toast.success(`${data.email} promosso ad Agenzia`);
      setPromoTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setPromoBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.set("search", search);
      if (type !== "all") params.set("type", type);
      if (includeDeleted) params.set("include_deleted", "true");
      const { data } = await axios.get(`${API}/admin/workspaces?${params}`, { withCredentials: true });
      setItems(data.items); setTotal(data.total);
    } catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, type, includeDeleted]);

  const handleRestore = async (ws) => {
    try {
      await axios.post(`${API}/admin/workspaces/${ws.id}/restore`, {}, { withCredentials: true });
      toast.success("Workspace ripristinato"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const handleDelete = async () => {
    if (!delTarget || delName !== delTarget.name) { toast.error("Nome workspace non corrisponde"); return; }
    if (!delCheck) { toast.error("Conferma il checkbox"); return; }
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/workspaces/${delTarget.id}`, { withCredentials: true });
      toast.success("Workspace eliminato (recuperabile 30gg)");
      setDelTarget(null); setDelName(""); setDelCheck(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4" data-testid="admin-workspaces-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#0D1F3C]">Console Admin · Workspaces</h1>
          <p className="text-sm text-[#0D1F3C]/60 mt-1">{total} workspace · pagina {page}/{totalPages}</p>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="ws-deleted" checked={includeDeleted} onCheckedChange={setIncludeDeleted} data-testid="ws-toggle-deleted" />
          <label htmlFor="ws-deleted" className="text-sm text-[#0D1F3C]/70 cursor-pointer">Mostra eliminati</label>
        </div>
      </div>
      <div className="bg-white border border-[#0D1F3C]/8 rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/40" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
            placeholder="Cerca per nome o ragione sociale..." className="pl-9" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            <SelectItem value="self">Self</SelectItem>
            <SelectItem value="client">Client</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => { setPage(1); load(); }}>Cerca</Button>
      </div>
      <div className="bg-white border border-[#0D1F3C]/8 rounded-xl overflow-hidden">
        <div className="max-h-[calc(100vh-340px)] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0D1F3C] text-white text-left sticky top-0">
              <tr>
                <th className="px-4 py-3 font-semibold">Nome</th>
                <th className="px-4 py-3 font-semibold">Tipo account</th>
                <th className="px-4 py-3 font-semibold">WS type</th>
                <th className="px-4 py-3 font-semibold">Owner email</th>
                <th className="px-4 py-3 font-semibold text-center">Cand</th>
                <th className="px-4 py-3 font-semibold text-center">Dip</th>
                <th className="px-4 py-3 font-semibold text-center">Pos</th>
                <th className="px-4 py-3 font-semibold">Creato</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={10} className="text-center py-8 text-[#0D1F3C]/40">Caricamento…</td></tr> :
               items.length === 0 ? <tr><td colSpan={10} className="text-center py-8 text-[#0D1F3C]/40">Nessun workspace</td></tr> :
               items.map((w) => (
                <tr key={w.id} className={`border-t border-[#0D1F3C]/5 hover:bg-[#F4F7FB] ${w.deleted_at ? "opacity-60" : ""}`}>
                  <td className="px-4 py-3 font-medium text-[#0D1F3C]">{w.name || "—"}</td>
                  <td className="px-4 py-3" data-testid={`ws-account-type-${w.id}`}>
                    {w.owner_account_type === "agency" ? (
                      <Badge className="bg-[#1B4FD8] text-white">AGENZIA</Badge>
                    ) : w.owner_account_type === "personal" ? (
                      <Badge variant="outline" className="border-purple-300 text-purple-700">PERSONAL</Badge>
                    ) : (
                      <Badge className="bg-slate-200 text-slate-700">AZIENDA</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline">{w.type}</Badge></td>
                  <td className="px-4 py-3 text-[#0D1F3C]/80 text-xs">{w.owner_email || "—"}</td>
                  <td className="px-4 py-3 text-center">{w.candidates_count}</td>
                  <td className="px-4 py-3 text-center">{w.employees_count}</td>
                  <td className="px-4 py-3 text-center">{w.positions_count}</td>
                  <td className="px-4 py-3 text-xs text-[#0D1F3C]/60">
                    {w.created_at ? new Date(w.created_at).toLocaleDateString("it-IT") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {w.deleted_at ? (
                      <Badge className="bg-red-100 text-red-700">Eliminato</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {w.deleted_at ? (
                      <Button size="sm" variant="ghost" onClick={() => handleRestore(w)} data-testid={`ws-restore-${w.id}`}>
                        <ArrowCounterClockwise size={14} className="mr-1" />Ripristina
                      </Button>
                    ) : (
                      <div className="flex flex-wrap justify-end gap-1">
                        {w.type !== "client" && w.owner_account_type !== "agency" && (
                          <Button size="sm" variant="ghost" className="text-[#1B4FD8] hover:bg-blue-50"
                            onClick={() => setPromoTarget(w)} data-testid={`ws-promote-${w.id}`}>
                            <ArrowCircleUp size={14} className="mr-1" />Promuovi ad Agenzia
                          </Button>
                        )}
                        {w.type !== "client" && w.owner_account_type === "agency" && (
                          <Button size="sm" variant="ghost" className="text-purple-700 hover:bg-purple-50"
                            onClick={() => { setDemoTarget(w); setDemoForce(false); }}
                            data-testid={`ws-demote-${w.id}`}>
                            <ArrowCircleDown size={14} className="mr-1" />Declassa ad Azienda
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-700 hover:bg-red-50"
                          onClick={() => { setDelTarget(w); setDelName(""); setDelCheck(false); }}
                          data-testid={`ws-delete-${w.id}`}>
                          <Trash size={14} className="mr-1" />Elimina
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 bg-[#F4F7FB] border-t border-[#0D1F3C]/5">
          <span className="text-xs text-[#0D1F3C]/60">{total} risultati totali</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}><CaretLeft size={14} /></Button>
            <span className="text-sm font-medium">{page} / {totalPages}</span>
            <Button size="sm" variant="outline" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}><CaretRight size={14} /></Button>
          </div>
        </div>
      </div>

      <Dialog open={!!promoTarget} onOpenChange={(o) => !o && setPromoTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <ArrowCircleUp size={20} weight="duotone" className="text-[#1B4FD8]" />
            Promuovi ad Agenzia
          </DialogTitle></DialogHeader>
          {promoTarget && (
            <div className="space-y-3 text-sm">
              <p className="text-[#0D1F3C]/80">
                Stai per promuovere l'owner del workspace <strong>{promoTarget.name}</strong> ({promoTarget.owner_email})
                a account <strong>Agenzia</strong>.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs space-y-1.5">
                <p className="font-semibold text-[#0D1F3C]">Effetti della promozione:</p>
                <ul className="list-disc list-inside text-[#0D1F3C]/70 space-y-0.5">
                  <li>Sblocca la creazione di workspace clienti (multi-workspace)</li>
                  <li>Mostra il selector workspace nella sidebar</li>
                  <li>Aggiorna metadata Stripe customer (NO change pricing tier)</li>
                  <li>Operazione tracciata nell'Audit Log</li>
                </ul>
              </div>
              <p className="text-xs text-[#0D1F3C]/50">
                L'utente vedrà la modifica al prossimo refresh della sessione. Operazione idempotente: re-eseguirla è sicuro.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setPromoTarget(null)} disabled={promoBusy} data-testid="promote-cancel">Annulla</Button>
                <Button onClick={handlePromote} disabled={promoBusy} className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="promote-confirm">
                  {promoBusy ? "Promozione…" : "Promuovi ad Agenzia"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!demoTarget} onOpenChange={(o) => !o && setDemoTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <ArrowCircleDown size={20} weight="duotone" className="text-purple-700" />
            Declassa ad Azienda
          </DialogTitle></DialogHeader>
          {demoTarget && (
            <div className="space-y-3 text-sm">
              <p className="text-[#0D1F3C]/80">
                Stai per declassare l'owner del workspace <strong>{demoTarget.name}</strong> ({demoTarget.owner_email})
                da <strong>Agenzia</strong> a <strong>Azienda</strong> single-workspace.
              </p>
              <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-xs space-y-1.5">
                <p className="font-semibold text-amber-900">Effetti del declassamento:</p>
                <ul className="list-disc list-inside text-amber-900 space-y-0.5">
                  <li>Nasconde il selector workspace nella sidebar</li>
                  <li>I workspace clienti esistenti diventano orfani (l'owner non potrà più accedervi)</li>
                  <li>Operazione tracciata nell'Audit Log</li>
                </ul>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="demote-force-ws" checked={demoForce} onCheckedChange={setDemoForce} data-testid="demote-force-ws-check" />
                <label htmlFor="demote-force-ws" className="text-xs text-[#0D1F3C]/80 cursor-pointer">
                  Confermo: declassa anche se esistono workspace clienti attivi (force)
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDemoTarget(null)} disabled={demoBusy} data-testid="demote-ws-cancel">Annulla</Button>
                <Button onClick={handleDemote} disabled={demoBusy} variant="destructive" data-testid="demote-ws-confirm">
                  {demoBusy ? "Declassamento…" : "Declassa ad Azienda"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Elimina workspace — Conferma</DialogTitle></DialogHeader>
          {delTarget && (
            <div className="space-y-3 text-sm">
              <p>Stai per eliminare il workspace <strong>{delTarget.name}</strong> (owner: {delTarget.owner_email}). Recuperabile per 30 giorni.</p>
              <div>
                <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Digita il nome del workspace per confermare</label>
                <Input value={delName} onChange={(e) => setDelName(e.target.value)} placeholder={delTarget.name} data-testid="ws-delete-confirm-name" />
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="ws-hd" checked={delCheck} onCheckedChange={setDelCheck} data-testid="ws-delete-confirm-check" />
                <label htmlFor="ws-hd" className="text-xs text-[#0D1F3C]/80 cursor-pointer">Confermo: hard-delete dopo 30 giorni</label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDelTarget(null)} disabled={busy}>Annulla</Button>
                <Button variant="destructive" onClick={handleDelete} disabled={busy || delName !== delTarget.name || !delCheck} data-testid="ws-delete-confirm-btn">
                  Elimina
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
